import Accordion from '@carvana/ds-accordion';
import Link from '@carvana/ds-link';
import cx from 'classnames';
import { Phone } from 'lucide-react';
import { useState } from 'react';
import serviceFaq from '~/constants/dealership/serviceFaq';
import useServicePhone from '~/hooks/dealership/selectors/useServicePhone';
import styles from './ServiceFAQ.module.css';

const categories = [
  'All',
  'Pricing',
  'Scheduling',
  'Services',
  'Warranty'
] as const;

export default function ServiceFAQ() {
  const [activeCategory, setActiveCategory] = useState('All');
  const servicePhone = useServicePhone();

  const filtered =
    activeCategory === 'All'
      ? serviceFaq
      : serviceFaq.filter(item => item.category === activeCategory);

  return (
    <section className="bg-canvas-default px-lg py-4xl md:px-2xl md:py-[96px]">
      <div className="mx-auto grid max-w-[1272px] grid-cols-1 gap-3xl lg:grid-cols-[380px_1fr] lg:gap-[72px]">
        <div>
          <div className="mb-lg text-text-primary t-label-xs tracking-widest">
            FREQUENTLY ASKED QUESTIONS
          </div>
          <h2 className="mb-xl text-text-strong t-heading-3xl">
            Straight answers to real questions.
          </h2>
          <p className="mb-2xl text-text-default t-body-md">
            Everything we get asked most often, grouped so you can jump to what
            matters.
          </p>

          <select
            value={activeCategory}
            onChange={e => setActiveCategory(e.target.value)}
            className="mb-2xl w-full rounded-lg border border-border-subtle bg-canvas-default px-lg py-sm text-text-strong t-strong-sm lg:hidden"
            data-adobe-id="service-faq-category-mobile"
            data-analytics-id="Service FAQ - Category Mobile"
            data-testid="faq-category-mobile"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category} (
                {category === 'All'
                  ? serviceFaq.length
                  : serviceFaq.filter(f => f.category === category).length}
                )
              </option>
            ))}
          </select>

          <div className="hidden flex-col gap-[6px] lg:flex">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-adobe-id={`service-faq-category-${category.toLowerCase()}`}
                className={cx(
                  styles.categoryButton,
                  activeCategory === category
                    ? 'text-text-primary bg-background-primary-muted'
                    : 'text-text-default bg-transparent'
                )}
                data-analytics-id={`Service FAQ - Category ${category}`}
                data-testid={`faq-category-${category.toLowerCase()}`}
              >
                <span>{category}</span>
                <span className="text-text-subtle t-body-xs">
                  {category === 'All'
                    ? serviceFaq.length
                    : serviceFaq.filter(f => f.category === category).length}
                </span>
              </button>
            ))}
          </div>

          {servicePhone && (
            <div className="mt-3xl hidden rounded-xl border border-border-subtle bg-canvas-muted p-2xl lg:block">
              <div className="mb-xs text-text-strong t-strong-sm">
                Still have questions?
              </div>
              <p className="mb-lg text-text-default t-body-xs">
                Our service team is glad to help.
              </p>
              <Link
                href={`tel:${servicePhone.number}`}
                size="sm"
                preventVisitedColor
                className="inline-flex items-center gap-x-sm"
                data-adobe-id="click-to-call-service-faq"
                data-analytics-id="Service FAQ - Call Phone"
                data-testid="faq-call-phone"
              >
                <Phone className="size-[14px]" />
                Call {servicePhone.number}
              </Link>
            </div>
          )}
        </div>

        <div className="flex flex-col pt-xs">
          {filtered.map(item => (
            <Accordion
              key={item.question}
              classes={{ container: 'py-0', header: 'py-2xl' }}
              toggleStyle="caret"
              headerText={item.question}
              testId={`service-faq-${item.question
                .slice(0, 20)
                .replace(/\s+/g, '-')
                .toLowerCase()}`}
            >
              <p className="max-w-[680px] pb-2xl text-text-default t-body-md">
                {item.answer}
              </p>
            </Accordion>
          ))}
        </div>
      </div>
    </section>
  );
}
